import { useNow } from '@vueuse/core'
import { computed } from 'vue'

import { syncService } from '@/services/syncService'
import { useEventStore } from '@/stores/eventStore'

export interface SyncDrainProgress {
  done: number
  total: number
  /** 1..99 while the drain runs — never 100 until every item is confirmed. */
  percent: number
  /** What the in-flight batch is doing, or null when nothing is in flight. */
  phase: 'uploading…' | 'processing on server…' | null
}

/**
 * Queue-drain progress for the current sync episode ("4 of 11 synced"), shared by the
 * Sync status dialog and the ambient top-edge progress bar (sync-feedback.md §4.2).
 *
 * Baseline comes from `syncService.drainTotal`; the live queue length gives the remainder.
 * An in-flight batch contributes PARTIAL credit per item — a batch is not synced until the
 * server confirms it:
 *   - uploading: byte fraction × 0.6 (upload is at most 60% of an item's journey)
 *   - processing on server: 0.6 → 0.95 asymptotically over time (server work is
 *     unmeasurable from the client, but a visibly creeping number tells the user the app
 *     is alive — the phase label tells them why it's slow)
 */
export function useSyncDrainProgress() {
  const eventStore = useEventStore()
  const now = useNow({ interval: 1000 })

  const drainProgress = computed<SyncDrainProgress | null>(() => {
    const total = syncService.drainTotal.value
    if (total === null || total < 1) return null
    const status = syncService.syncStatus.value
    if (status !== 'syncing' && status !== 'error') return null
    const remaining = Math.min(eventStore.unsyncedEvents.length, total)
    if (remaining === 0) return null
    const done = total - remaining

    const batchCount = syncService.currentBatchCount.value
    const uploadFraction = syncService.currentBatchUploadFraction.value
    const uploadedAt = syncService.currentBatchUploadedAt.value

    let itemWeight = uploadFraction * 0.6
    if (uploadedAt !== null) {
      // Asymptotic creep toward 0.95 while the server processes (~63% of the way per minute)
      const elapsedSeconds = Math.max(0, (now.value.getTime() - uploadedAt) / 1000)
      itemWeight = 0.6 + 0.35 * (1 - Math.exp(-elapsedSeconds / 60))
    }

    const percent = Math.min(99, ((done + batchCount * itemWeight) / total) * 100)
    const phase =
      batchCount === 0 ? null : uploadedAt === null ? 'uploading…' : 'processing on server…'
    return { done, total, percent: Math.max(1, percent), phase }
  })

  return { drainProgress }
}
